// ══════════════════════════════════════════════════════════════════════════════
// SELDOR — DataMappingPage
// Interface for user to confirm/reject fuzzy-matched ingredient aliases.
// This is the "Data Translator" layer — ensures analytics run on clean data.
// ══════════════════════════════════════════════════════════════════════════════

import { useState, useEffect, useCallback } from 'react';
import {
  Link2, Check, X, Search, Edit3, AlertTriangle,
  CheckCircle2, Zap, RefreshCw, ArrowRight
} from 'lucide-react';
import { useTheme } from '@contexts/ThemeContext';
import { useLang } from '@contexts/LangContext';
import { useAuth } from '@contexts/AuthContext';
import { supabase } from '@lib/supabase';
import { AliasResolver } from '@services/aliasResolver';
import { classifyConfidence } from '@utils/fuzzyMatcher';
import { PageHeader } from '@components/ui/PageHeader';
import {
  Card, Button, Input, Select, Badge, Grid
} from '@components/ui/Primitives';
import { StatCard } from '@components/ui/StatCard';
import { LoadingSpinner, EmptyState } from '@components/ui/States';
import { Modal } from '@components/ui/Modal';

export function DataMappingPage() {
  const { theme } = useTheme();
  const { t, lang, isRTL } = useLang();
  const { effectiveProfile } = useAuth();

  const [pendingAliases, setPendingAliases] = useState([]);
  const [confirmedAliases, setConfirmedAliases] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('pending');
  const [searchQuery, setSearchQuery] = useState('');
  const [manualModal, setManualModal] = useState(null);
  const [testInput, setTestInput] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);

  const tenantId = effectiveProfile?.tenant_id;

  const loadData = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    const [pending, confirmed, { data: ings }] = await Promise.all([
      AliasResolver.getPendingMappings(tenantId),
      AliasResolver.getConfirmedMappings(tenantId),
      supabase.from('ingredients').select('id, name_ar, name_en, unit, purchase_price').eq('tenant_id', tenantId)
    ]);
    setPendingAliases(pending.data);
    setConfirmedAliases(confirmed.data);
    setIngredients(ings || []);
    setLoading(false);
  }, [tenantId]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleTestResolve = async () => {
    if (!testInput.trim()) return;
    setTesting(true);
    const result = await AliasResolver.resolve(testInput, tenantId);
    setTestResult(result);
    setTesting(false);
  };

  const handleConfirm = async (alias) => {
    const canonical = {
      id: alias.canonical_ingredient_id,
      name_ar: alias.canonical_name_ar,
      name_en: alias.canonical_name_en
    };
    await AliasResolver.confirmMapping({
      tenantId,
      rawText: alias.alias_text,
      canonical,
      source: alias.source === 'auto_fuzzy' ? 'auto_fuzzy' : 'manual',
      score: parseFloat(alias.confidence)
    });
    await loadData();
  };

  const handleReject = async (alias) => {
    await AliasResolver.rejectSuggestion({ tenantId, rawText: alias.alias_text });
    await loadData();
  };

  const handleManualMap = async (canonicalId) => {
    if (!manualModal || !canonicalId) return;
    const canonical = ingredients.find(i => i.id === canonicalId);
    if (!canonical) return;
    await AliasResolver.confirmMapping({
      tenantId,
      rawText: manualModal.rawText,
      canonical,
      source: 'manual',
      score: 1.0
    });
    setManualModal(null);
    await loadData();
  };

  const handleDeleteConfirmed = async (alias) => {
    if (!window.confirm(lang === 'ar' ? 'هل تريد حذف هذا الربط؟' : 'Delete this mapping?')) return;
    await supabase.from('ingredient_aliases').delete().eq('id', alias.id);
    await loadData();
  };

  if (loading) return <LoadingSpinner />;

  const filteredPending = pendingAliases.filter(a =>
    (a.alias_text || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (a.canonical_name_ar || '').toLowerCase().includes(searchQuery.toLowerCase())
  );
  const filteredConfirmed = confirmedAliases.filter(a =>
    (a.alias_text || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (a.canonical_name_ar || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  const confidenceBadgeVariant = (conf) => {
    const label = classifyConfidence(parseFloat(conf));
    if (label === 'high') return 'success';
    if (label === 'medium') return 'warning';
    if (label === 'low') return 'danger';
    return 'neutral';
  };

  return (
    <div>
      <PageHeader
        icon={<Link2 size={22} color={theme.primary} />}
        title={t.dataMappingTitle}
        description={t.dataMappingDesc}
        actions={
          <Button variant="outline" onClick={loadData}>
            <RefreshCw size={14} /> {t.refresh}
          </Button>
        }
      />

      <div style={{ marginBottom: 24 }}>
        <Grid min={220}>
          <StatCard
            icon={<AlertTriangle size={20} color={theme.warning} />}
            label={t.pendingMappings}
            value={pendingAliases.length}
            accentColor={theme.warning}
          />
          <StatCard
            icon={<CheckCircle2 size={20} color={theme.success} />}
            label={t.confirmedMappings}
            value={confirmedAliases.length}
            accentColor={theme.success}
          />
          <StatCard
            icon={<Zap size={20} color={theme.primary} />}
            label={lang === 'ar' ? 'تطابقات تلقائية' : 'Auto-matches'}
            value={confirmedAliases.filter(a => a.source === 'auto_fuzzy').length}
          />
        </Grid>
      </div>

      <Card style={{ padding: 20, marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
          <Search size={16} color={theme.primary} />
          <span style={{ fontSize: 14, fontWeight: 700, color: theme.text }}>{t.testMapping}</span>
        </div>
        <p style={{ margin: '0 0 12px', fontSize: 12, color: theme.textSecondary }}>
          {t.testMappingDesc}
        </p>
        <div style={{ display: 'flex', gap: 10, alignItems: 'stretch' }}>
          <Input
            value={testInput}
            onChange={e => setTestInput(e.target.value)}
            placeholder={lang === 'ar' ? 'مثال: حليب نادك طازج 1L' : 'Example: Nadec fresh milk 1L'}
            onKeyDown={e => e.key === 'Enter' && handleTestResolve()}
          />
          <Button onClick={handleTestResolve} disabled={!testInput || testing}>
            {testing ? '...' : t.testMapping}
          </Button>
        </div>

        {testResult && (
          <div style={{
            marginTop: 14,
            padding: 14,
            background: theme.bgInput,
            borderRadius: 10,
            border: `1px solid ${theme.border}`
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 12, color: theme.textTertiary }}>{t.rawInput}:</span>
              <span style={{ fontSize: 13, fontWeight: 600, color: theme.text }}>"{testResult.input}"</span>
              <ArrowRight size={14} color={theme.textTertiary} style={{ transform: isRTL ? 'scaleX(-1)' : 'none' }} />
              {testResult.canonical ? (
                <>
                  <span style={{ fontSize: 13, fontWeight: 700, color: theme.primary }}>
                    {lang === 'ar' ? testResult.canonical.name_ar : testResult.canonical.name_en}
                  </span>
                  <Badge variant={confidenceBadgeVariant(testResult.confidenceScore || 1)}>
                    {((testResult.confidenceScore || 1) * 100).toFixed(0)}%
                  </Badge>
                </>
              ) : (
                <Badge variant="danger">{lang === 'ar' ? 'لم يُحَلّ' : 'Unresolved'}</Badge>
              )}
            </div>
            <div style={{ fontSize: 11, color: theme.textTertiary }}>
              {lang === 'ar' ? 'الحالة' : 'Status'}: <strong style={{ color: theme.textSecondary }}>{testResult.status}</strong>
              {testResult.source && <> · {lang === 'ar' ? 'المصدر' : 'Source'}: <strong style={{ color: theme.textSecondary }}>{testResult.source}</strong></>}
            </div>
            {testResult.suggestions && testResult.suggestions.length > 0 && (
              <div style={{ marginTop: 10 }}>
                <div style={{ fontSize: 11, color: theme.textTertiary, marginBottom: 6 }}>
                  {lang === 'ar' ? 'اقتراحات بديلة:' : 'Alternative suggestions:'}
                </div>
                {testResult.suggestions.slice(0, 3).map((s, i) => (
                  <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12, marginBottom: 4 }}>
                    <span style={{ color: theme.text }}>
                      {lang === 'ar' ? s.canonical.name_ar : s.canonical.name_en}
                    </span>
                    <Badge variant={confidenceBadgeVariant(s.score)}>
                      {(s.score * 100).toFixed(0)}%
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </Card>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, borderBottom: `1px solid ${theme.border}` }}>
        {[
          { key: 'pending', label: `${t.pendingMappings} (${pendingAliases.length})` },
          { key: 'confirmed', label: `${t.confirmedMappings} (${confirmedAliases.length})` }
        ].map(tb => (
          <div
            key={tb.key}
            onClick={() => setTab(tb.key)}
            style={{
              padding: '10px 16px',
              cursor: 'pointer',
              fontSize: 13,
              fontWeight: 600,
              color: tab === tb.key ? theme.primary : theme.textSecondary,
              borderBottom: tab === tb.key ? `2px solid ${theme.primary}` : '2px solid transparent',
              marginBottom: -1
            }}
          >
            {tb.label}
          </div>
        ))}
      </div>

      <div style={{ marginBottom: 14 }}>
        <Input
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          placeholder={lang === 'ar' ? 'بحث...' : 'Search...'}
        />
      </div>

      {tab === 'pending' && (
        filteredPending.length === 0 ? (
          <EmptyState
            icon={<CheckCircle2 size={36} color={theme.success} />}
            title={lang === 'ar' ? 'لا ربط معلّق' : 'No pending mappings'}
            description={lang === 'ar' ? 'كل البيانات نظيفة' : 'All data is clean'}
          />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {filteredPending.map(alias => (
              <Card key={alias.id} style={{ padding: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 220 }}>
                    <div style={{ fontSize: 11, color: theme.textTertiary, marginBottom: 3 }}>{t.rawInput}</div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: theme.text }}>
                      {alias.alias_text}
                    </div>
                  </div>

                  <ArrowRight size={16} color={theme.textTertiary} style={{ transform: isRTL ? 'scaleX(-1)' : 'none' }} />

                  <div style={{ flex: 1, minWidth: 220 }}>
                    <div style={{ fontSize: 11, color: theme.textTertiary, marginBottom: 3 }}>{t.suggestedMatch}</div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: theme.primary }}>
                      {lang === 'ar' ? alias.canonical_name_ar : alias.canonical_name_en}
                    </div>
                  </div>

                  <div style={{ textAlign: 'center' }}>
                    <Badge variant={confidenceBadgeVariant(alias.confidence)}>
                      {(parseFloat(alias.confidence) * 100).toFixed(0)}%
                    </Badge>
                    <div style={{ fontSize: 10, color: theme.textTertiary, marginTop: 4 }}>
                      {alias.occurrences} {t.occurrences}
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: 6 }}>
                    <Button size="sm" onClick={() => handleConfirm(alias)}>
                      <Check size={13} /> {t.confirmMatch}
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setManualModal({ rawText: alias.alias_text, currentAlias: alias })}>
                      <Edit3 size={13} /> {t.manualMap}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleReject(alias)}>
                      <X size={13} color={theme.danger} />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )
      )}

      {tab === 'confirmed' && (
        filteredConfirmed.length === 0 ? (
          <EmptyState
            title={lang === 'ar' ? 'لا ربط مؤكد بعد' : 'No confirmed mappings yet'}
            description={lang === 'ar' ? 'أكّد الربط المعلّق لبناء الكتالوج' : 'Confirm pending mappings to build your catalog'}
          />
        ) : (
          <Card style={{ overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${theme.border}` }}>
                    {[t.rawInput, t.suggestedMatch, t.matchScore, t.occurrences, t.actions].map(h => (
                      <th key={h} style={{
                        padding: '10px 14px',
                        textAlign: isRTL ? 'right' : 'left',
                        fontSize: 11,
                        fontWeight: 600,
                        color: theme.textTertiary
                      }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredConfirmed.map(alias => (
                    <tr key={alias.id} style={{ borderBottom: `1px solid ${theme.borderLight}` }}>
                      <td style={{ padding: '12px 14px', fontSize: 13, color: theme.text }}>
                        {alias.alias_text}
                      </td>
                      <td style={{ padding: '12px 14px', fontSize: 13, fontWeight: 600, color: theme.primary }}>
                        {lang === 'ar' ? alias.canonical_name_ar : alias.canonical_name_en}
                      </td>
                      <td style={{ padding: '12px 14px' }}>
                        <Badge variant={confidenceBadgeVariant(alias.confidence)}>
                          {(parseFloat(alias.confidence) * 100).toFixed(0)}%
                        </Badge>
                      </td>
                      <td style={{ padding: '12px 14px', fontSize: 13, color: theme.textSecondary }}>
                        {alias.occurrences}
                      </td>
                      <td style={{ padding: '12px 14px' }}>
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteConfirmed(alias)}>
                          <X size={13} color={theme.danger} />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )
      )}

      <Modal
        open={!!manualModal}
        onClose={() => setManualModal(null)}
        title={t.manualMap}
      >
        {manualModal && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, color: theme.textTertiary, marginBottom: 4 }}>{t.rawInput}</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: theme.text }}>{manualModal.rawText}</div>
            </div>
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: theme.textSecondary, marginBottom: 6 }}>
                {t.selectCanonical}
              </div>
              <Select
                value=""
                onChange={e => handleManualMap(e.target.value)}
                options={[
                  { value: '', label: t.none },
                  ...ingredients.map(i => ({
                    value: i.id,
                    label: lang === 'ar' ? i.name_ar : i.name_en
                  }))
                ]}
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <Button variant="ghost" onClick={() => setManualModal(null)}>{t.cancel}</Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
